package com.example.mystybag.appCore.logic.pages

import com.example.mystybag.appCore.model.pages.AddEmployeeUIState
import com.example.mystybag.common.resources.SR
import com.example.mystybag.communication.common.CommonCommands
import com.example.mystybag.communication.utils.Response
import com.example.mystybag.communication.utils.onResponse
import com.example.mystybag.core.defines.UtilsPreferencesDefines.PREFERENCES_MERCHANT_ID
import com.example.mystybag.core.expected.Preferences
import com.example.mystybag.core.expected.sharedString
import com.example.mystybag.core.extensions.isValidEmail
import com.example.mystybag.core.logic.base.AppContext
import com.example.mystybag.core.logic.base.ChildViewModel
import com.example.mystybag.core.model.CountryItem
import com.example.mystybag.sharedmodels.project.requestModels.merchant.CreateMerchantStaffRequestModel
import com.example.mystybag.sharedmodels.project.requestModels.merchant.GetMerchantLocationsRequestModel
import kotlinx.coroutines.launch

class AddEmployeeViewModel(
    context: AppContext
) : ChildViewModel<AddEmployeeUIState>(context, AddEmployeeUIState()) {

    override fun onCreate() {
        navControls.isVisible = true
        navControls.backButtonVisible = true
        navControls.title = sharedString(SR.strings.add_employee)
        loadCountries()
        loadLocations()
    }

    private fun loadLocations() {
        val merchantId = Preferences.getLong(key = PREFERENCES_MERCHANT_ID, 0L)
        if (merchantId == 0L) return
        scope.launch {
            commandBlock(showProgress = false) {
                CommonCommands.getMerchantLocations(
                    GetMerchantLocationsRequestModel(merchantId = merchantId)
                )
                    .onResponse(
                        success = { response ->
                            launchOnMain {
                                val responseModel = response as? com.example.mystybag.sharedmodels.project.responseModels.merchant.GetMerchantLocationsResponseModel
                                updateState {
                                    it.copy(locations = responseModel?.locations ?: emptyList())
                                }
                            }
                        }
                    )
            }
        }
    }

    fun toggleLocation(locationId: Long) {
        updateState { state ->
            val newSet = if (state.selectedLocationIds.contains(locationId)) {
                state.selectedLocationIds - locationId
            } else {
                state.selectedLocationIds + locationId
            }
            state.copy(
                selectedLocationIds = newSet,
                locationsIsError = false,
                locationsErrorMsg = ""
            )
        }
    }

    private fun loadCountries() {
        scope.launch {
            commandBlock(showProgress = false) {
                CommonCommands.sendGetCountries()
                    .onResponse(
                        success = { response ->
                            launchOnMain {
                                val countries = response.countriesList?.map { country ->
                                    CountryItem(
                                        id = country.id,
                                        name = country.name,
                                        dialCode = country.dialCode,
                                        isoAlpha2 = country.isoAlpha2
                                    )
                                } ?: emptyList()
                                updateState { it.copy(countries = countries) }
                            }
                        }
                    )
            }
        }
    }

    fun showDialCodePicker() {
        updateState { it.copy(showDialCodePicker = true) }
    }

    fun hideDialCodePicker() {
        updateState { it.copy(showDialCodePicker = false) }
    }

    fun selectDialCodeCountry(dialCode: String) {
        updateState {
            it.copy(
                dialCode = dialCode,
                showDialCodePicker = false,
                dialCodeIsError = false,
                dialCodeErrorMsg = ""
            )
        }
    }

    fun onEmailChange(value: String) {
        updateState { it.copy(email = value, emailIsError = false, emailErrorMsg = "") }
    }

    fun onFirstNameChange(value: String) {
        updateState { it.copy(firstName = value, firstNameIsError = false, firstNameErrorMsg = "") }
    }

    fun onLastNameChange(value: String) {
        updateState { it.copy(lastName = value, lastNameIsError = false, lastNameErrorMsg = "") }
    }

    fun onPhoneNumberChange(value: String) {
        // Strip leading 0 or +359 / 359; allow only digits after that
        var cleaned = value
        while (cleaned.startsWith("0")) cleaned = cleaned.drop(1)
        if (cleaned.startsWith("+359")) cleaned = cleaned.drop(4)
        else if (cleaned.startsWith("359")) cleaned = cleaned.drop(3)
        cleaned = cleaned.filter { it.isDigit() }
        updateState { it.copy(phoneNumber = cleaned, phoneNumberIsError = false, phoneNumberErrorMsg = "") }
    }

    fun onRoleChange(role: String) {
        updateState { it.copy(role = role) }
    }

    fun onCanCreateBagsChange(value: Boolean) {
        updateState { it.copy(canCreateBags = value) }
    }

    fun onCanViewRevenueChange(value: Boolean) {
        updateState { it.copy(canViewRevenue = value) }
    }

    /** Clears the form after successful add so the user can add another employee. Keeps locations and countries. */
    private fun resetFormForNextEmployee() {
        updateState { current ->
            AddEmployeeUIState(
                email = "",
                firstName = "",
                lastName = "",
                phoneNumber = "",
                dialCode = current.dialCode,
                role = current.role,
                countries = current.countries,
                locations = current.locations,
                selectedLocationIds = current.selectedLocationIds,
                canCreateBags = current.canCreateBags,
                canViewRevenue = current.canViewRevenue
            )
        }
    }

    /** Navigate back to the staff list (so user can always exit the add-employee screen). */
    fun goBackToStaffList() {
        goBack()
    }

    fun onSubmit() {
        if (!validate()) return
        scope.launch {
            updateState { it.copy(isLoading = true) }
            commandBlock(showProgress = true) {
                CommonCommands.createMerchantStaff(
                    CreateMerchantStaffRequestModel(
                        email = uiState.value.email,
                        firstName = uiState.value.firstName,
                        lastName = uiState.value.lastName,
                        phoneNumber = uiState.value.phoneNumber,
                        dialCode = uiState.value.dialCode,
                        role = uiState.value.role,
                        merchantLocationIds = uiState.value.selectedLocationIds.toList(),
                        canCreateBags = uiState.value.canCreateBags,
                        canViewRevenue = uiState.value.canViewRevenue
                    )
                )
                    .onResponse(
                        success = {
                            launchOnMain {
                                updateState { it.copy(isLoading = false) }
                                showSnackbar(sharedString(SR.strings.employee_added_successfully))
                                resetFormForNextEmployee()
                            }
                        },
                        baseError = { response ->
                            launchOnMain {
                                updateState { it.copy(isLoading = false) }
                                showSnackbar(Response.getErrorMessage(response.status, response.apiEndPoint, response.statusMessage))
                            }
                        }
                    )
            }
        }
    }

    private fun validate(): Boolean {
        var valid = true
        val s = uiState.value

        if (s.email.isEmpty() || !s.email.isValidEmail) {
            updateState {
                it.copy(
                    emailIsError = true,
                    emailErrorMsg = sharedString(SR.strings.please_enter_valid_email_address)
                )
            }
            valid = false
        }
        if (s.firstName.isBlank()) {
            updateState {
                it.copy(
                    firstNameIsError = true,
                    firstNameErrorMsg = sharedString(SR.strings.please_enter_valid_email_address)
                )
            }
            valid = false
        }
        if (s.lastName.isBlank()) {
            updateState {
                it.copy(
                    lastNameIsError = true,
                    lastNameErrorMsg = sharedString(SR.strings.please_enter_valid_email_address)
                )
            }
            valid = false
        }
        if (s.dialCode.isBlank()) {
            updateState {
                it.copy(
                    dialCodeIsError = true,
                    dialCodeErrorMsg = sharedString(SR.strings.please_enter_valid_email_address)
                )
            }
            valid = false
        }
        if (s.phoneNumber.isBlank()) {
            updateState {
                it.copy(
                    phoneNumberIsError = true,
                    phoneNumberErrorMsg = sharedString(SR.strings.please_enter_valid_email_address)
                )
            }
            valid = false
        }
        if (s.selectedLocationIds.isEmpty()) {
            updateState {
                it.copy(
                    locationsIsError = true,
                    locationsErrorMsg = sharedString(SR.strings.select_at_least_one_location)
                )
            }
            valid = false
        }

        return valid
    }
}
